// File path: src/utils/jobStorage.js
// Purpose: Handles saving, loading, updating, deleting, searching, and opening saved ManuCalc manufacturing plans.

import { MATERIALS } from "../data/manufacturingKnowledge";
import { normalizeJobForRiskEngine } from "./jobRiskEngine";
import { queueCloudJobDelete, queueCloudJobSave } from "./workspaceCloudSync";

const STORAGE_KEY = "manucalc_jobs_v2";
const LEGACY_STORAGE_KEY = "manucalc_jobs_v1";
const RECENT_JOBS_KEY = "manucalc_recent_jobs_v1";
const LAST_OPEN_JOB_KEY = "manucalc_last_open_job_v1";
const CURRENT_SCHEMA_VERSION = 2;

const nowIso = () => new Date().toISOString();

const createId = (prefix = "id") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const safeParse = (value, fallback = []) => {
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const ensureNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const defaultDimensions = (fallback = {}) => ({
  length: ensureNumber(fallback.length, 1),
  width: ensureNumber(fallback.width, 1),
  height: ensureNumber(fallback.height, 1),
});

const defaultStock = (fallback = {}) => ({
  length: ensureNumber(fallback.length, 1.25),
  width: ensureNumber(fallback.width, 1.25),
  height: ensureNumber(fallback.height, 1.25),
});

const normalizeFile = (file = {}) => ({
  id: file.id || createId("file"),
  name: file.name || "Untitled File",
  size: ensureNumber(file.size, 0),
  type: file.type || "unknown",
  category: file.category || "Attachment",
  uploadedAt: file.uploadedAt || nowIso(),
  previewUrl: file.previewUrl || "",
  dataUrl: file.dataUrl || "",
  fileDataUrl: file.fileDataUrl || "",
  fileUrl: file.fileUrl || "",
  storagePath: file.storagePath || "",
  notes: file.notes || "",
});

const normalizePart = (part = {}, job = {}, index = 0) => ({
  id: part.id || createId("part"),
  name: part.name || (index === 0 ? job.name || "Primary Part" : `Part ${index + 1}`),
  partNumber: part.partNumber || (index === 0 ? job.partNumber || "" : ""),
  quantity: Math.max(1, ensureNumber(part.quantity, index === 0 ? job.quantity || 1 : 1)),
  materialId: part.materialId || job.materialId || MATERIALS[0]?.id || "aluminum-6061",
  dimensions: defaultDimensions(part.dimensions || (index === 0 ? job.dimensions : {})),
  stock: defaultStock(part.stock || (index === 0 ? job.stock : {})),
  files: ensureArray(part.files).map(normalizeFile),
  notes: part.notes || "",
});

const normalizeOperation = (operation = {}, index = 0) => ({
  id: operation.id || createId("op"),
  name: operation.name || `Operation ${index + 1}`,
  operationTypeId: operation.operationTypeId || operation.operationType || "facing",
  machineId: operation.machineId || operation.machine || "cnc-mill-3-axis",
  setupNumber: Math.max(1, ensureNumber(operation.setupNumber, index + 1)),
  workPerformed: operation.workPerformed || "",
  toolDescription: operation.toolDescription || "",
  toolsUsed: operation.toolsUsed || "",
  inspectionCriteria: operation.inspectionCriteria || "",
  images: ensureArray(operation.images).map(normalizeFile),
  toolMaterial: operation.toolMaterial || "Carbide",
  toolDiameter: ensureNumber(operation.toolDiameter, 0.5),
  stockDiameter: ensureNumber(operation.stockDiameter || operation.stockDiameterX, 1),
  fluteCount: Math.max(1, ensureNumber(operation.fluteCount, 4)),
  depthOfCut: ensureNumber(operation.depthOfCut, 0.05),
  widthOfCut: ensureNumber(operation.widthOfCut, 0.1),
  setupHours: ensureNumber(operation.setupHours, 0.5),
  runHours: ensureNumber(operation.runHours, 0.5),
  machineHourlyRate: operation.machineHourlyRate === "" ? "" : ensureNumber(operation.machineHourlyRate, 0),
  laborHourlyRate: operation.laborHourlyRate === "" ? "" : ensureNumber(operation.laborHourlyRate, 0),
  coolant: operation.coolant || "Flood coolant",
  notes: operation.notes || "",
  riskNotes: ensureArray(operation.riskNotes),
});

const normalizeProcess = (process = {}, job = {}) => ({
  name: process.name || job.name || "Manufacturing Process",
  owner: process.owner || "",
  revision: process.revision || job.revision || "A",
  objective:
    process.objective ||
    `Manufacture ${job.name || "this job"} to the current customer requirements.`,
});

const normalizeCalculation = (calculation = {}, index = 0) => ({
  id: calculation.id || createId("calc"),
  toolId: calculation.toolId || "",
  toolName: calculation.toolName || calculation.toolId || `Calculation ${index + 1}`,
  summary: calculation.summary || "Saved calculation",
  data: calculation.data || calculation.rawData || null,
  partId: calculation.partId || "",
  partName: calculation.partName || "",
  customer: calculation.customer || "",
  createdAt: calculation.createdAt || nowIso(),
  updatedAt: calculation.updatedAt || calculation.createdAt || nowIso(),
});

const normalizeCosting = (costing = {}) => ({
  materialCost: costing.materialCost === "" ? "" : ensureNumber(costing.materialCost, 0),
  defaultLaborRate: costing.defaultLaborRate === "" ? "" : ensureNumber(costing.defaultLaborRate, 75),
  additionalCost: costing.additionalCost === "" ? "" : ensureNumber(costing.additionalCost, 0),
});

export const normalizeStoredJob = (job = {}) => {
  const riskNormalized = normalizeJobForRiskEngine(job || {});
  const baseJob = {
    ...job,
    ...riskNormalized,
  };

  const fallbackPart = {
    id: "primary-part",
    name: baseJob.name || "Primary Part",
    partNumber: baseJob.partNumber || "",
    quantity: baseJob.quantity || 1,
    materialId: baseJob.materialId || MATERIALS[0]?.id || "aluminum-6061",
    dimensions: baseJob.dimensions,
    stock: baseJob.stock,
    files: ensureArray(baseJob.files),
    notes: "",
  };

  const partsSource = ensureArray(baseJob.parts).length ? baseJob.parts : [fallbackPart];
  const normalizedParts = partsSource.map((part, index) => normalizePart(part, baseJob, index));
  const primaryPart = normalizedParts[0];

  return {
    ...baseJob,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: baseJob.id || createId("job"),
    name: baseJob.name || "Untitled Manufacturing Job",
    customer: baseJob.customer || "",
    partNumber: baseJob.partNumber || primaryPart.partNumber || "",
    revision: baseJob.revision || "",
    quantity: Math.max(1, ensureNumber(baseJob.quantity, primaryPart.quantity || 1)),
    materialId: baseJob.materialId || primaryPart.materialId || MATERIALS[0]?.id || "aluminum-6061",
    dimensions: defaultDimensions(baseJob.dimensions || primaryPart.dimensions),
    stock: defaultStock(baseJob.stock || primaryPart.stock),
    parts: normalizedParts,
    files: [],
    operations: ensureArray(baseJob.operations).map(normalizeOperation),
    process: normalizeProcess(baseJob.process, baseJob),
    calculations: ensureArray(baseJob.calculations).map(normalizeCalculation),
    costing: normalizeCosting(baseJob.costing),
    notes: baseJob.notes || "",
    createdAt: baseJob.createdAt || nowIso(),
    updatedAt: baseJob.updatedAt || nowIso(),
  };
};

const readRawJobs = () => {
  const currentRaw = localStorage.getItem(STORAGE_KEY);
  if (currentRaw) return safeParse(currentRaw, []);

  const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
  if (legacyRaw) return safeParse(legacyRaw, []);

  return [];
};

const writeJobs = (jobs) => {
  const normalizedJobs = ensureArray(jobs).map(normalizeStoredJob);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedJobs));
  return normalizedJobs;
};

export const mergeCloudJobs = (jobs) => {
  const normalizedJobs = writeJobs(jobs);
  window.dispatchEvent(new CustomEvent("manucalc:jobs-updated"));
  return normalizedJobs;
};

export const getAllJobs = () => {
  const jobs = readRawJobs();
  const normalizedJobs = ensureArray(jobs).map(normalizeStoredJob);

  if (JSON.stringify(jobs) !== JSON.stringify(normalizedJobs)) {
    writeJobs(normalizedJobs);
  }

  return normalizedJobs.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
};

export const getJobById = (jobId) => {
  if (!jobId) return null;
  const jobs = getAllJobs();
  return jobs.find((job) => job.id === jobId) || null;
};

export const saveJob = (job) => {
  const normalizedJob = normalizeStoredJob({
    ...job,
    updatedAt: nowIso(),
  });

  const jobs = getAllJobs();
  const existingIndex = jobs.findIndex((j) => j.id === normalizedJob.id);

  if (existingIndex !== -1) {
    jobs[existingIndex] = {
      ...jobs[existingIndex],
      ...normalizedJob,
      createdAt: jobs[existingIndex].createdAt || normalizedJob.createdAt,
      updatedAt: nowIso(),
    };
  } else {
    jobs.push({
      ...normalizedJob,
      createdAt: normalizedJob.createdAt || nowIso(),
      updatedAt: nowIso(),
    });
  }

  writeJobs(jobs);
  markJobOpened(normalizedJob.id);
  queueCloudJobSave(normalizedJob);
  window.dispatchEvent(new CustomEvent("manucalc:jobs-updated"));
  return normalizedJob;
};

export const patchJob = (jobId, updates = {}) => {
  const existingJob = getJobById(jobId);
  if (!existingJob) return null;
  return saveJob({ ...existingJob, ...updates });
};

export const deleteJob = (jobId) => {
  const jobs = getAllJobs().filter((job) => job.id !== jobId);
  writeJobs(jobs);

  const recent = getRecentJobIds().filter((id) => id !== jobId);
  localStorage.setItem(RECENT_JOBS_KEY, JSON.stringify(recent));

  if (localStorage.getItem(LAST_OPEN_JOB_KEY) === jobId) {
    localStorage.removeItem(LAST_OPEN_JOB_KEY);
  }
  queueCloudJobDelete(jobId);
  window.dispatchEvent(new CustomEvent("manucalc:jobs-updated"));
};

export const duplicateJob = (jobId) => {
  const job = getJobById(jobId);
  if (!job) return null;

  const duplicatedParts = ensureArray(job.parts).map((part, index) => ({
    ...part,
    id: createId("part"),
    name: index === 0 ? `${part.name || "Primary Part"} (Copy)` : part.name,
    files: ensureArray(part.files).map((file) => ({ ...file, id: createId("file") })),
  }));

  const duplicatedOperations = ensureArray(job.operations).map((operation) => ({
    ...operation,
    id: createId("op"),
  }));

  const newJob = normalizeStoredJob({
    ...job,
    id: createId("job"),
    name: `${job.name || "Untitled Job"} (Copy)`,
    parts: duplicatedParts,
    operations: duplicatedOperations,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  });

  saveJob(newJob);
  return newJob;
};

export const clearAllJobs = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
  localStorage.removeItem(RECENT_JOBS_KEY);
  localStorage.removeItem(LAST_OPEN_JOB_KEY);
};

export const searchJobs = (query) => {
  const jobs = getAllJobs();
  const q = String(query || "").trim().toLowerCase();

  if (!q) return jobs;

  return jobs.filter((job) => {
    const searchableText = [
      job.name,
      job.partNumber,
      job.customer,
      job.revision,
      job.materialId,
      ...(job.machineRoute || []),
      ...(job.parts || []).flatMap((part) => [
        part.name,
        part.partNumber,
        part.materialId,
        ...(part.files || []).map((file) => file.name),
      ]),
      ...(job.operations || []).map((operation) => operation.name || operation.operationTypeId || ""),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(q);
  });
};

export const markJobOpened = (jobId) => {
  if (!jobId) return;

  localStorage.setItem(LAST_OPEN_JOB_KEY, jobId);

  const recent = getRecentJobIds().filter((id) => id !== jobId);
  const nextRecent = [jobId, ...recent].slice(0, 8);
  localStorage.setItem(RECENT_JOBS_KEY, JSON.stringify(nextRecent));
};

export const getRecentJobIds = () => {
  const raw = localStorage.getItem(RECENT_JOBS_KEY);
  return ensureArray(raw ? safeParse(raw, []) : []);
};

export const getRecentJobs = () => {
  const jobs = getAllJobs();
  const recentIds = getRecentJobIds();

  return recentIds
    .map((id) => jobs.find((job) => job.id === id))
    .filter(Boolean);
};

export const getLastOpenJob = () => {
  const jobId = localStorage.getItem(LAST_OPEN_JOB_KEY);
  return jobId ? getJobById(jobId) : null;
};

export const getJobStats = () => {
  const jobs = getAllJobs();

  return {
    totalJobs: jobs.length,
    totalParts: jobs.reduce((sum, job) => sum + ensureArray(job.parts).length, 0),
    totalFiles: jobs.reduce(
      (sum, job) =>
        sum +
        ensureArray(job.parts).reduce(
          (partSum, part) => partSum + ensureArray(part.files).length,
          0
        ),
      0
    ),
    totalOperations: jobs.reduce((sum, job) => sum + ensureArray(job.operations).length, 0),
    recentlyOpened: getRecentJobs().length,
    lastOpenJob: getLastOpenJob(),
  };
};
